import { useEffect, useRef, useState, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  mergeClasses,
} from '@coreai/fluentui-react';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useNodeEditorFocusTrap } from './shared/useNodeEditorFocusTrap';

// Resizable width bounds. The drawer is a configuration surface, so it stays
// comfortably readable at the low end and never fully eclipses the canvas at
// the high end (the upper bound is additionally clamped to the viewport below).
const MIN_DRAWER_WIDTH = 420;
const DEFAULT_DRAWER_WIDTH = 720;
const MAX_DRAWER_WIDTH = 1200;
// Keep at least this much of the designer region visible to the left so the
// drawer can't be dragged over the entire workspace.
const VIEWPORT_RESERVE = 280;
const KEYBOARD_RESIZE_STEP = 24;
const WIDTH_STORAGE_KEY = 'otto:nodeEditorDrawerWidth';

const clampDrawerWidth = (width: number): number => {
  const upper = effectiveMaxDrawerWidth();
  return Math.round(Math.max(MIN_DRAWER_WIDTH, Math.min(width, upper)));
};

/**
 * The maximum width the drawer can actually reach on the current viewport,
 * clamped to `[MIN_DRAWER_WIDTH, MAX_DRAWER_WIDTH]`. Exposed so the resize
 * handle can advertise the correct `aria-valuemax` — a static `MAX_DRAWER_WIDTH`
 * would lie to assistive tech on narrow viewports where `VIEWPORT_RESERVE`
 * pulls the ceiling below 1200.
 */
const effectiveMaxDrawerWidth = (): number => {
  const viewportMax =
    typeof window !== 'undefined'
      ? window.innerWidth - VIEWPORT_RESERVE
      : MAX_DRAWER_WIDTH;
  return Math.max(MIN_DRAWER_WIDTH, Math.min(MAX_DRAWER_WIDTH, viewportMax));
};

const readStoredWidth = (): number => {
  if (typeof window === 'undefined') return DEFAULT_DRAWER_WIDTH;
  const stored = Number(window.localStorage?.getItem(WIDTH_STORAGE_KEY));
  // Clamp both branches through `clampDrawerWidth` so the initial `aria-valuenow`
  // matches the width actually rendered — even on narrow viewports where the
  // stored/default 720 would otherwise be squeezed by `VIEWPORT_RESERVE`.
  return stored && !Number.isNaN(stored)
    ? clampDrawerWidth(stored)
    : clampDrawerWidth(DEFAULT_DRAWER_WIDTH);
};

const useStyles = makeStyles({
  // Right-anchored slide-over drawer panel. Width is set inline (resizable) and
  // clamped between MIN_DRAWER_WIDTH and the viewport-aware maximum.
  drawer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    maxWidth: '100%',
    zIndex: 1001,
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopLeftRadius: tokens.borderRadiusXLarge,
    borderBottomLeftRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow64,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    visibility: 'hidden',
    transform: 'translateX(100%)',
    transitionProperty: 'transform, visibility',
    transitionDuration: tokens.durationGentle,
    transitionTimingFunction: tokens.curveDecelerateMax,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '1ms',
    },
  },
  drawerVisible: {
    visibility: 'visible',
    transform: 'translateX(0)',
  },

  // Left-edge drag handle for resizing. A thin visual seam with a wider
  // invisible hit area; brand-tinted on hover/focus/drag. Rendered as an ARIA
  // separator so it is keyboard-operable (Arrow keys) and screen-reader aware.
  resizeHandle: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: '10px',
    transform: 'translateX(-50%)',
    zIndex: 1002,
    cursor: 'col-resize',
    touchAction: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    backgroundColor: 'transparent',
    // The visible seam.
    '::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '2px',
      borderRadius: tokens.borderRadiusSmall,
      backgroundColor: 'transparent',
      transitionProperty: 'background-color',
      transitionDuration: tokens.durationFast,
      transitionTimingFunction: tokens.curveEasyEase,
    },
    ':hover::after': {
      backgroundColor: tokens.colorNeutralStroke1Hover,
    },
    ':focus-visible': {
      outline: 'none',
    },
    ':focus-visible::after': {
      backgroundColor: tokens.colorBrandStroke1,
      width: '3px',
    },
  },
  resizeHandleDragging: {
    '::after': {
      backgroundColor: tokens.colorBrandStroke1,
      width: '3px',
    },
  },

  // Content area
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
  },
});

interface NodeEditorDrawerProps {
  children: React.ReactNode;
}

/**
 * NodeEditorDrawer — right-side slide-over container for the node editor.
 *
 * **Non-modal by design.** Unlike {@link NodeEditorModal}, the drawer is a
 * side panel that coexists with the workflow behind it: the canvas stays
 * visible AND interactive while the drawer is open. Clicking a different
 * node on the canvas swaps the drawer's contents; clicking on empty canvas
 * pans as usual. There is intentionally no invisible dismiss layer over the
 * canvas — dismissal is via the Close button in the header, the Escape key,
 * or the natural swap-on-node-click behavior.
 *
 * Consequences for a11y (see `useNodeEditorFocusTrap`):
 *  - `role="complementary"` (not `dialog` + `aria-modal`) — AT users hear
 *    "complementary region" instead of "modal dialog", matching what sighted
 *    users see.
 *  - Tab escapes to the canvas; there is no Tab trap.
 *  - Focus is still saved on open and restored on close.
 *  - Escape still closes.
 *
 * The legacy modal is preserved as the opt-in fallback when the
 * `nodeEditorLegacyModal` feature flag is on. That modal-vs-drawer selection is
 * made in {@link NodeEditor}, which renders exactly one of the two shells; this
 * component only ever renders on the drawer path and does not read the flag.
 */
export const NodeEditorDrawer = ({ children }: NodeEditorDrawerProps) => {
  const styles = useStyles();
  const { containerRef, handleKeyDown } =
    useNodeEditorFocusTrap<HTMLDivElement>({ modal: false });

  const isOpen = useNodeEditorStore((s) => s.isOpen);

  const [width, setWidth] = useState<number>(readStoredWidth);
  const [isDragging, setIsDragging] = useState(false);
  // The effective upper bound is viewport-dependent (`window.innerWidth -
  // VIEWPORT_RESERVE`, clamped to [MIN, MAX]). Tracked in state so
  // `aria-valuemax` on the resize handle stays truthful as the viewport
  // shrinks — a static `MAX_DRAWER_WIDTH` would announce 1200 to screen
  // readers on a viewport that actually caps at 700.
  const [maxWidth, setMaxWidth] = useState<number>(effectiveMaxDrawerWidth);
  const dragState = useRef<{ startX: number; startWidth: number } | null>(null);

  // Persist the chosen width so it survives editor open/close and reloads.
  // Writes are suppressed while the user is actively dragging so we don't
  // hammer localStorage with 60–120 synchronous writes per second (which
  // would block the main thread and cause visible drag jank). When
  // `isDragging` flips false — either after a pointer drag ends or between
  // keyboard-driven / reset transitions that never enter the drag state at
  // all — the effect fires once with the final width.
  useEffect(() => {
    if (isDragging) return;
    try {
      window.localStorage?.setItem(WIDTH_STORAGE_KEY, String(width));
    } catch {
      // localStorage may throw in private-browsing / storage-disabled
      // contexts; a lost persisted width is preferable to a broken drawer.
    }
  }, [width, isDragging]);

  // Re-clamp against the viewport on resize so a width saved on a wide screen
  // can't leave the drawer wider than a subsequently narrower window. Also
  // updates the announced `aria-valuemax`.
  useEffect(() => {
    const onResize = () => {
      const nextMax = effectiveMaxDrawerWidth();
      setMaxWidth(nextMax);
      setWidth((w) => clampDrawerWidth(w));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      dragState.current = { startX: e.clientX, startWidth: width };
      e.currentTarget.setPointerCapture(e.pointerId);
      setIsDragging(true);
    },
    [width],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragState.current) return;
      // Anchored to the right: dragging the handle left (smaller clientX) widens.
      const delta = dragState.current.startX - e.clientX;
      setWidth(clampDrawerWidth(dragState.current.startWidth + delta));
    },
    [],
  );

  const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragState.current) return;
    dragState.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setIsDragging(false);
    // Flipping isDragging false triggers the persistence effect once with
    // the final width, avoiding per-pointer-move localStorage writes.
  }, []);

  const handleHandleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          setWidth((w) => clampDrawerWidth(w + KEYBOARD_RESIZE_STEP));
          break;
        case 'ArrowRight':
          e.preventDefault();
          setWidth((w) => clampDrawerWidth(w - KEYBOARD_RESIZE_STEP));
          break;
        case 'Home':
          e.preventDefault();
          setWidth(clampDrawerWidth(MAX_DRAWER_WIDTH));
          break;
        case 'End':
          e.preventDefault();
          setWidth(MIN_DRAWER_WIDTH);
          break;
        default:
          break;
      }
    },
    [],
  );

  // Double-click the handle to restore the default width.
  const handleReset = useCallback(() => {
    setWidth(clampDrawerWidth(DEFAULT_DRAWER_WIDTH));
  }, []);

  return (
    <>
      {/* Drawer — non-modal, no dismiss layer over the canvas so users keep
          full workflow context while configuring a node. */}
      <div
        ref={containerRef}
        className={mergeClasses(styles.drawer, isOpen && styles.drawerVisible)}
        style={{ width }}
        role="complementary"
        aria-label="Node configuration"
        aria-hidden={!isOpen}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        {/* Resize handle */}
        <div
          className={mergeClasses(
            styles.resizeHandle,
            isDragging && styles.resizeHandleDragging,
          )}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize node configuration panel"
          aria-valuemin={MIN_DRAWER_WIDTH}
          aria-valuemax={maxWidth}
          aria-valuenow={width}
          tabIndex={0}
          data-a11y-skip-focus-trap="true"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={handleHandleKeyDown}
          onDoubleClick={handleReset}
        />
        <div className={styles.content}>{children}</div>
      </div>
    </>
  );
};

NodeEditorDrawer.displayName = 'NodeEditorDrawer';
